import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { casesApi } from "../api/cases";
import type {
  Case,
  CaseFilters,
  CreateCaseRequest,
  UpdateCaseStateRequest,
  CaseState,
} from "../types/case";
import { queryKeys } from "../api/queryKeys";
import { invalidateCases } from "../api/queryInvalidation";
import { useTenantId } from "./useTenantId";

/** @deprecated Use `queryKeys.cases` from `../api/queryKeys` instead */
export const caseQueryKeys = queryKeys.cases;

// ================================
// Query Hooks
// ================================

/**
 * Hook for fetching case inbox with filters
 */
export const useCaseInbox = (
  filters: CaseFilters = {},
  options?: {
    enabled?: boolean;
    refetchInterval?: number;
  },
) => {
  const tenantId = useTenantId();

  return useQuery({
    queryKey: queryKeys.cases.inbox(tenantId, filters),
    queryFn: () => casesApi.getInbox(filters),
    enabled: options?.enabled,
    refetchInterval: options?.refetchInterval,
    staleTime: 30000, // Consider data fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });
};

/**
 * Hook for fetching a specific case
 */
export const useCaseDetail = (
  id: string,
  options?: {
    enabled?: boolean;
  },
) => {
  const tenantId = useTenantId();
  const numericId = id ? parseInt(id, 10) : null;
  return useQuery({
    queryKey: queryKeys.cases.detail(tenantId, numericId as number),
    queryFn: () => casesApi.getById(numericId as number),
    enabled: options?.enabled !== false && !!id,
    staleTime: 10000, // Consider data fresh for 10 seconds
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });
};

/**
 * Hook for fetching dashboard metrics
 */
export const useDashboardMetrics = (
  companyId: string,
  options?: {
    enabled?: boolean;
    refetchInterval?: number;
  },
) => {
  const tenantId = useTenantId();

  return useQuery({
    queryKey: queryKeys.cases.dashboard(tenantId),
    queryFn: () => casesApi.getDashboardMetrics(companyId),
    enabled: options?.enabled !== false && !!companyId,
    refetchInterval: options?.refetchInterval || 60000, // Refetch every minute
    staleTime: 30000,
  });
};

/**
 * Hook for fetching manager dashboard metrics
 */
export const useManagerDashboard = (
  companyId: string,
  options?: {
    enabled?: boolean;
    refetchInterval?: number;
  },
) => {
  const tenantId = useTenantId();

  return useQuery({
    queryKey: queryKeys.cases.managerDashboard(tenantId),
    queryFn: () => casesApi.getManagerDashboard(companyId),
    enabled: options?.enabled !== false && !!companyId,
    refetchInterval: options?.refetchInterval || 60000,
    staleTime: 30000,
  });
};

/**
 * Hook for fetching overdue cases
 */
export const useOverdueCases = (
  companyId: string,
  options?: {
    enabled?: boolean;
  },
) => {
  const tenantId = useTenantId();

  return useQuery({
    queryKey: queryKeys.cases.overdue(tenantId),
    queryFn: () => casesApi.getOverdueCases(companyId),
    enabled: options?.enabled !== false && !!companyId,
    staleTime: 60000, // Consider data fresh for 1 minute
  });
};

/**
 * Hook for fetching cases due today
 */
export const useCasesDueToday = (
  companyId: string,
  options?: {
    enabled?: boolean;
  },
) => {
  const tenantId = useTenantId();

  return useQuery({
    queryKey: queryKeys.cases.dueToday(tenantId),
    queryFn: () => casesApi.getCasesDueToday(companyId),
    enabled: options?.enabled !== false && !!companyId,
    staleTime: 300000, // Consider data fresh for 5 minutes
  });
};

/**
 * Hook for fetching cases due this week
 */
export const useCasesDueThisWeek = (
  companyId: string,
  options?: {
    enabled?: boolean;
  },
) => {
  const tenantId = useTenantId();

  return useQuery({
    queryKey: queryKeys.cases.dueThisWeek(tenantId),
    queryFn: () => casesApi.getCasesDueThisWeek(companyId),
    enabled: options?.enabled !== false && !!companyId,
    staleTime: 300000,
  });
};

/**
 * Hook for fetching allowed state transitions for a case
 */
export const useAllowedTransitions = (
  caseId: string,
  options?: {
    enabled?: boolean;
  },
) => {
  const tenantId = useTenantId();
  const numericId = caseId ? parseInt(caseId, 10) : null;
  return useQuery({
    queryKey: queryKeys.cases.allowedTransitions(tenantId, numericId as number),
    queryFn: () => casesApi.getAllowedTransitions(numericId as number),
    enabled: options?.enabled !== false && !!caseId,
    staleTime: 60000,
  });
};

// ================================
// Mutation Hooks
// ================================

/**
 * Hook for creating a new case
 */
export const useCreateCase = (options?: {
  onSuccess?: (data: Case) => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();

  return useMutation({
    mutationFn: (caseData: CreateCaseRequest) => casesApi.create(caseData),
    onSuccess: (data) => {
      // Invalidate all case queries (inbox, dashboards, etc.)
      invalidateCases(queryClient, tenantId);

      // Add the new case to the cache
      queryClient.setQueryData(queryKeys.cases.detail(tenantId, data.id), data);

      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
};

/**
 * Hook for updating case state
 */
export const useUpdateCaseState = (options?: {
  onSuccess?: (data: Case) => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();

  return useMutation({
    mutationFn: ({
      id,
      stateUpdate,
    }: {
      id: string;
      stateUpdate: UpdateCaseStateRequest;
    }) => casesApi.updateStatus(parseInt(id, 10), stateUpdate),
    onSuccess: (data: Case) => {
      // Update the case in the cache
      queryClient.setQueryData(queryKeys.cases.detail(tenantId, data.id), data);

      // Invalidate related queries
      invalidateCases(queryClient, tenantId);

      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
};

/**
 * Hook for updating case status via state integer (PATCH /case-status)
 */
export const useUpdateCaseStatus = (options?: {
  onSuccess?: (data: Case) => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();

  return useMutation({
    mutationFn: ({
      id,
      state,
      comment,
    }: {
      id: number;
      state: CaseState;
      comment?: string;
    }) => casesApi.updateCaseStatus(id, state, comment),
    onSuccess: (data: Case) => {
      queryClient.setQueryData(queryKeys.cases.detail(tenantId, data.id), data);
      invalidateCases(queryClient, tenantId);
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
};

/**
 * Hook for reverting a case from COMPLIANCE_SVP_INTERVIEW back to its previous status
 */
export const useRevertSvpStatus = (options?: {
  onSuccess?: (data: Case) => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();

  return useMutation({
    mutationFn: ({ id, comment }: { id: number; comment?: string }) =>
      casesApi.revertSvpStatus(id, comment),
    onSuccess: (data: Case) => {
      queryClient.setQueryData(queryKeys.cases.detail(tenantId, data.id), data);
      invalidateCases(queryClient, tenantId);
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
};

/**
 * Hook for soft-deleting a case via the status update endpoint (status: 4)
 */
export const useSoftDeleteCase = (options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();

  return useMutation({
    mutationFn: (id: number) => casesApi.softDelete(id, tenantId),
    onSuccess: () => {
      invalidateCases(queryClient, tenantId);
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
};

/**
 * Hook for updating case details
 */
export const useUpdateCase = (options?: {
  onSuccess?: (data: Case) => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();

  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<
        Pick<Case, "title" | "description" | "priority" | "dueDate">
      >;
    }) => casesApi.update(id, updates),
    onSuccess: (data) => {
      // Update the case in the cache
      queryClient.setQueryData(queryKeys.cases.detail(tenantId, data.id), data);

      // Invalidate inbox queries
      invalidateCases(queryClient, tenantId);

      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
};

/**
 * Hook for deleting a case
 */
export const useDeleteCase = (options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();

  return useMutation({
    mutationFn: (id: string) => casesApi.delete(parseInt(id, 10)),
    onSuccess: (_, deletedId) => {
      const numericId = parseInt(deletedId, 10);
      // Remove the case from all caches
      queryClient.removeQueries({
        queryKey: queryKeys.cases.detail(tenantId, numericId),
      });

      // Invalidate list queries
      invalidateCases(queryClient, tenantId);

      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
};

/**
 * Hook for bulk state updates
 */
export const useBulkUpdateState = (options?: {
  onSuccess?: (data: Case[]) => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();

  return useMutation({
    mutationFn: ({
      caseIds,
      newState,
      reason,
    }: {
      caseIds: string[];
      newState: string;
      reason?: string;
    }) => casesApi.bulkUpdateStatus(caseIds, newState, reason),
    onSuccess: (data: Case[]) => {
      // Update individual cases in cache
      data.forEach((caseItem) => {
        queryClient.setQueryData(
          queryKeys.cases.detail(tenantId, caseItem.id),
          caseItem,
        );
      });

      // Invalidate list queries
      invalidateCases(queryClient, tenantId);

      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
};

/**
 * Hook for bulk case deletion
 */
export const useBulkDeleteCases = (options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();

  return useMutation({
    mutationFn: (caseIds: number[]) => casesApi.bulkDelete(caseIds),
    onSuccess: (_, deletedIds: number[]) => {
      // Remove cases from cache
      deletedIds.forEach((id) => {
        const numericId = typeof id === "string" ? parseInt(id, 10) : id;
        queryClient.removeQueries({
          queryKey: queryKeys.cases.detail(tenantId, numericId),
        });
      });

      // Invalidate list queries
      invalidateCases(queryClient, tenantId);

      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
};

// ================================
// Utility Hooks
// ================================

/**
 * Hook for optimistic case state updates
 */
export const useOptimisticCaseState = () => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();

  const optimisticUpdate = (caseId: string, newState: string | number) => {
    const numericId = parseInt(caseId, 10);
    queryClient.setQueryData(
      queryKeys.cases.detail(tenantId, numericId),
      (oldData: Case | undefined) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          state: (typeof newState === "string"
            ? Number(newState)
            : newState) as Case["state"],
          updatedAt: new Date().toISOString(),
        };
      },
    );
  };

  const rollbackUpdate = (caseId: string) => {
    const numericId = parseInt(caseId, 10);
    queryClient.invalidateQueries({
      queryKey: queryKeys.cases.detail(tenantId, numericId),
    });
  };

  return { optimisticUpdate, rollbackUpdate };
};

/**
 * Hook for prefetching case detail
 */
export const usePrefetchCaseDetail = () => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();

  return (caseId: string) => {
    const numericId = parseInt(caseId, 10);
    queryClient.prefetchQuery({
      queryKey: queryKeys.cases.detail(tenantId, numericId),
      queryFn: () => casesApi.getById(numericId),
      staleTime: 10000,
    });
  };
};

/**
 * Hook for invalidating all case queries
 */
export const useInvalidateCaseQueries = () => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();

  return () => {
    invalidateCases(queryClient, tenantId);
  };
};

/**
 * Fetch documents explicitly linked to a case (from case_documents table)
 */
export const useCaseDocuments = (
  caseId: number | null,
  params?: { page?: number; size?: number },
  options?: { enabled?: boolean },
) => {
  const tenantId = useTenantId();

  return useQuery({
    queryKey: queryKeys.cases.caseDocuments(tenantId, caseId as number, params),
    queryFn: () => casesApi.getCaseDocuments(caseId as number, params),
    enabled: options?.enabled !== false && !!caseId,
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
  });
};
